import { downloadFileViaFetch } from "lib/utils";
import { useFileExportStatus } from "./useFileExportStatus";

type ExportMutationLike<T extends { id: string }> = {
  data?: T | undefined;
  isLoading: boolean;
  reset: () => void;
};

type UseFileExportArgs<T extends { id: string }> = {
  /** A mutation that returns `{ id }` (the export id) — e.g. useExportReports. */
  mutation: ExportMutationLike<T>;
  /** Download filename fallback (used when the server omits Content-Disposition). */
  getFileName?: () => string | undefined;
  /** Called on successful completion, before the download (e.g. analytics). */
  onSuccess?: () => void;
  onError?: () => void;
};

/**
 * Drives the shared async file-export flow: once the mutation returns an export
 * id, subscribe to its `fileExport` status and download the file when it
 * completes. Returns whether an export is currently in progress.
 */
export function useFileExport<T extends { id: string }>({
  mutation,
  getFileName,
  onSuccess,
  onError,
}: UseFileExportArgs<T>) {
  const { data, isLoading, reset } = mutation;

  const { data: exportStatus } = useFileExportStatus({
    exportId: data?.id,
    onSuccess: async (downloadUrl) => {
      onSuccess?.();
      await downloadFileViaFetch(downloadUrl, getFileName?.());
      reset();
    },
    onError: () => {
      onError?.();
      reset();
    },
  });

  const isExporting =
    isLoading ||
    (!!data?.id && !exportStatus?.fileExport.isComplete && !exportStatus?.fileExport.isError);

  return { isExporting };
}
